// Cómo se pinta cada celda de la tabla de cartera. Puro: recibe el dato y devuelve etiqueta, tono
// y el motivo cuando no hay dato. Ningún ausente sale como cero ni como guion mudo sin explicación.
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import {
    CLIENT_LEVEL_LABEL,
    formatMoneyCents,
    NO_VALUE,
    type CreditTone,
} from '../creditFile/logic';
import { LOCK_READING_CELL, type LockReading } from '../lockPhase';
import { lockPhaseLabel } from '../mdm/logic';
import type { ArrearsBucket, PortfolioLockState } from './types';

export interface PortfolioCell {
    label: string;
    tone: CreditTone;
    /** Motivo del ausente, para el `title` de la celda. Vacío cuando el dato sí está. */
    title?: string;
}

/**
 * Lo que se pinta en una celda sin fuente. La celda mide 11-13% del ancho y la frase entera se
 * cortaría, así que va el token corto y el motivo completo en el `title` — el mismo reparto que
 * usa `CREDIT_STATE_CELL_SHORT` en la cartera del portal del vendedor.
 */
export const MISSING_COLUMN_CELL = 'Sin fuente';

export const NO_LEVEL_NOTE = 'Este crédito no registró el nivel del cliente al originarse.';

export const NO_ARREARS_NOTE =
    'Sin cuotas fechadas contra las cuales medir la mora: no se sabe, y no es cero.';

/** Token de la columna Saldo cuando no hay deuda que medir. Un `$0.00` diría «ya lo pagó». */
export const NO_BALANCE_CELL = 'No aplica';

export const NO_BALANCE_NOTE =
    'Este crédito no llegó a contraer la deuda: no hay saldo que cobrar, y eso no es cero.';

export function levelCell(level: ClientLevelEnum | undefined): PortfolioCell {
    if (!level) return { label: NO_VALUE, tone: 'neutral', title: NO_LEVEL_NOTE };
    return { label: CLIENT_LEVEL_LABEL[level], tone: 'neutral' };
}

/** El tono sube con el tramo: hasta 7 días informa, hasta 30 avisa, más de 30 alarma. */
export function arrearsCell(
    arrearsDays: number | null,
    status: LoanCreditStatusEnum,
): PortfolioCell {
    if (status === LoanCreditStatusEnum.SETTLED) {
        return {
            label: NO_VALUE,
            tone: 'neutral',
            title: 'Crédito liquidado: ya no acumula mora.',
        };
    }
    if (arrearsDays === null) {
        return { label: NO_VALUE, tone: 'neutral', title: NO_ARREARS_NOTE };
    }
    if (arrearsDays === 0) return { label: 'al día', tone: 'success' };
    if (arrearsDays > 30) return { label: `${String(arrearsDays)} d`, tone: 'danger' };
    if (arrearsDays > 7) return { label: `${String(arrearsDays)} d`, tone: 'warning' };
    return { label: `${String(arrearsDays)} d`, tone: 'info' };
}

/** Liquidado y «no aplica» son cosas distintas: uno pagó su deuda, el otro nunca la contrajo. */
export function balanceCell(
    outstandingBalanceCents: number | null,
    status: LoanCreditStatusEnum,
): PortfolioCell {
    if (status === LoanCreditStatusEnum.SETTLED) {
        return {
            label: NO_VALUE,
            tone: 'neutral',
            title: 'Crédito liquidado: sin saldo insoluto.',
        };
    }
    if (outstandingBalanceCents === null) {
        return { label: NO_BALANCE_CELL, tone: 'neutral', title: NO_BALANCE_NOTE };
    }
    return { label: formatMoneyCents(outstandingBalanceCents), tone: 'neutral' };
}

/** Qué mide la columna cuando el silo publica la deuda total. */
export const TOTAL_DEBT_COLUMN_NOTE =
    'Deuda total: capital, interés e IVA que faltan de las cuotas abiertas. Abajo, el saldo insoluto (solo capital).';

export const NO_TOTAL_DEBT_NOTE =
    'Este crédito no trae su deuda total: no se sabe cuánto debe, y no es cero.';

/** La deuda total del crédito, con las mismas reglas que el saldo: liquidado y «no aplica» aparte. */
export function totalDebtCell(
    totalDebtCents: number | null | undefined,
    status: LoanCreditStatusEnum,
): PortfolioCell {
    if (status === LoanCreditStatusEnum.SETTLED) {
        return { label: NO_VALUE, tone: 'neutral', title: 'Crédito liquidado: ya no debe nada.' };
    }
    if (totalDebtCents === undefined) {
        return { label: NO_VALUE, tone: 'neutral', title: NO_TOTAL_DEBT_NOTE };
    }
    if (totalDebtCents === null) {
        return { label: NO_BALANCE_CELL, tone: 'neutral', title: NO_BALANCE_NOTE };
    }
    return { label: formatMoneyCents(totalDebtCents), tone: 'neutral' };
}

/** El capital insoluto como dato secundario de la deuda total; `null` cuando no hay capital que decir. */
export function capitalSubLine(
    outstandingBalanceCents: number | null,
    status: LoanCreditStatusEnum,
): string | null {
    if (status === LoanCreditStatusEnum.SETTLED || outstandingBalanceCents === null) return null;
    return `cap. ${formatMoneyCents(outstandingBalanceCents)}`;
}

export interface CouponCell {
    label: string;
    progressPct: number;
}

/** «3/12» más la barra de avance. Cero pagados es un dato válido, no un vacío. */
export function couponCell(paidInstallments: number, termWeeks: number): CouponCell {
    return {
        label: `${String(paidInstallments)}/${String(termWeeks)}`,
        progressPct: termWeeks > 0 ? Math.round((paidInstallments / termWeeks) * 100) : 0,
    };
}

export const LOCK_STATE_PRESENTATION: Record<
    PortfolioLockState,
    { label: string; tone: CreditTone; title: string }
> = {
    locked: { label: 'Bloq', tone: 'danger', title: 'Equipo bloqueado por el MDM' },
    unlocked: { label: 'Sin bloq', tone: 'success', title: 'Equipo enrolado y sin bloqueo activo' },
    'no-device': {
        label: 'Sin disp',
        tone: 'neutral',
        title: 'Este crédito no tiene equipo enrolado en el MDM',
    },
    unknown: {
        label: 'Sin leer',
        tone: 'warning',
        title: 'No se pudo leer el expediente MDM: no se sabe si este equipo está bloqueado',
    },
    'no-permission': {
        label: 'Sin permiso',
        tone: 'info',
        title: 'No tienes permiso para ver el bloqueo de los equipos. Contacta al administrador',
    },
};

/** La celda Bloqueo: solo el candado confirmado dice «Bloq»; sin lectura, la pastilla del estado. */
export function lockCell(
    state: PortfolioLockState,
    reading: LockReading | null | undefined,
    scheduledFor?: string,
): { label: string; tone: CreditTone; title: string } {
    if (reading === 'locked') return LOCK_STATE_PRESENTATION.locked;
    if (!reading) return LOCK_STATE_PRESENTATION[state];
    const cell = LOCK_READING_CELL[reading];
    return reading === 'scheduled'
        ? { ...cell, title: lockPhaseLabel(reading, scheduledFor) }
        : cell;
}

/** Un conteo sin fuente se dice «—». Un `0` afirmaría que no hay ninguno, y eso no se sabe. */
export function statCell(count: number | null): string {
    return count === null ? NO_VALUE : String(count);
}

/** Rótulos del select de Bloqueo, con el copy del mock. Los `title` de arriba son de tooltip. */
export const LOCK_FILTER_LABEL: Record<PortfolioLockState, string> = {
    locked: 'Bloqueado',
    unlocked: 'Sin bloqueo',
    'no-device': 'Sin dispositivo',
    unknown: 'Sin leer',
    'no-permission': 'Sin permiso',
};

export const ARREARS_BUCKET_LABEL: Record<ArrearsBucket, string> = {
    none: 'Sin mora',
    '1-7': '1–7 días',
    '8-30': '8–30 días',
    '31-60': '31–60 días',
    '61-90': '61–90 días',
};

export const PORTFOLIO_STAT_LABEL = {
    all: 'Todos',
    current: 'Al día',
    arrears: 'En mora',
    settled: 'Liquidados',
    locked: 'Bloqueados',
} as const;

/** Cómo se tiñe el número de cada pastilla, siguiendo el diseño. */
export const PORTFOLIO_STAT_TONE = {
    all: 'neutral',
    current: 'success',
    arrears: 'danger',
    settled: 'info',
    locked: 'danger',
} as const satisfies Record<keyof typeof PORTFOLIO_STAT_LABEL, CreditTone>;
